import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';

const pick = (source, ...keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

const asList = payload => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const nested = pick(payload, 'items', 'Items', 'data', 'Data');
  if (Array.isArray(nested)) return nested;
  if (nested && typeof nested === 'object') return asList(nested);
  return [];
};

export const normalizeHpsReviseItem = raw => {
  if (!raw || typeof raw !== 'object') return null;
  const roleRaw = String(pick(raw, 'role', 'Role') ?? '').toLowerCase();
  const isEvaluator =
    roleRaw === 'evaluator' || pick(raw, 'isPertamina', 'IsPertamina') === true;
  const respondNotes = pick(raw, 'respondNotes', 'RespondNotes');
  const systemNote = pick(raw, 'systemNote', 'SystemNote');
  return {
    id: pick(raw, 'id', 'Id'),
    role: isEvaluator ? 'Evaluator' : 'Hps',
    respondNotes: respondNotes == null ? '' : String(respondNotes),
    systemNote: systemNote == null ? '' : String(systemNote),
    createdByLabel: pick(raw, 'createdByLabel', 'CreatedByLabel') ?? '',
    createdDate: pick(raw, 'createdDate', 'CreatedDate'),
  };
};

export const fetchHpsReviseThread = async procurementId => {
  const token = getAuthToken();
  if (!token || !procurementId) return [];

  const response = await fetch(
    `${api.API_URL}/hps-revise?procurementId=${encodeURIComponent(procurementId)}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.status === 404) return [];
  if (!response.ok) {
    const error = new Error(`Failed to load HPS revision (${response.status})`);
    error.status = response.status;
    error.data = await response.json().catch(() => null);
    throw error;
  }

  const result = await response.json();
  return asList(result?.data ?? result)
    .map(normalizeHpsReviseItem)
    .filter(Boolean);
};

export const postHpsRevise = async ({
  procurementId,
  role,
  respondNotes = '',
  statusHPS2,
}) => {
  const token = getAuthToken();
  if (!token || !procurementId) {
    throw new Error('Missing auth token or procurement id');
  }

  const response = await fetch(`${api.API_URL}/hps-revise`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      procurementId,
      role,
      respondNotes: String(respondNotes ?? '').trim(),
      statusHPS2,
    }),
  });

  if (!response.ok) {
    const error = new Error(`Failed to send HPS revision (${response.status})`);
    error.status = response.status;
    error.data = await response.json().catch(() => null);
    throw error;
  }

  const result = await response.json().catch(() => null);
  const created = normalizeHpsReviseItem(result?.data ?? result);
  return created?.id ? created : (result?.data ?? result);
};
